import { Menu, Modal } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { SidebarComponent } from '../components/antd_components/Layout/Sider'
import PerIcon from '../components/custom_components/Icon'
import { LoadingAndErrors } from '../components/custom_components/LoadingAndErrors'
import { useAuth } from '../context/auth-context'
import { useEmployeeLevels } from '../graphql/employee/hooks/useEmployeeLevels'
import { FeatureNameEnum } from '../graphql/featureToggle/FeatureNameEnum'
import useFeatureToggle from '../graphql/featureToggle/useFeatureToggle'
import { SmartAvatar } from '../modules/avatar/SmartAvatar'
import { MeetingsPagePermissions } from '../modules/meeting/permissions'
import { featureEnabled } from '../shared/featureToggles/feature-toggle-helper'
import { hasPermission } from '../shared/permissions/permission-helper'
import { Country, EmployeeLevelEnum, RoleName } from '../types'
import { SidebarPermissions } from './permissions'
import { useCompaniesAndCountriesOfCompanyManager } from '../graphql/company/hooks/useCompaniesAndCountriesOfCompanyManager'
import PerText from '../components/custom_components/Text'
import PerButton from '../components/custom_components/Button'
import useFeatureDisclaimerData from '../graphql/featureToggle/useFeatureDisclaimerData'
import { useAreKudosVisibleForMe } from '../graphql/kudos/useKudosQueries'
import { FeatureDisclaimerTextComponent } from '../shared/featureDisclaimer/FeatureDisclaimerTextComponent'
import '../shared/featureDisclaimer/FeatureDisclaimerTextComponent.less'

export const Sidebar: FC<{ collapsed; setCollapsed: (collapsed: boolean) => void }> = ({ collapsed, setCollapsed }) => {
  const { user } = useAuth()
  const location = useLocation()
  const [t] = useTranslation()
  const iconSize = 20
  const userRoles = user.roles
  const [isModalOpen, setIsModalOpen] = useState(true)

  const hrRolesList = !!userRoles ? userRoles.filter((role: RoleName) => role.startsWith('HR')) : []
  const isHrAdmin = hrRolesList.filter((role: RoleName) => role.includes('HR_')).some((role: RoleName) => role.split('_')[1] === 'ADMIN')
  const atLeastOneCountryVisible = (): boolean => {
    const hrRoles = userRoles ? userRoles.filter((role: RoleName) => role.includes('HR_')) : []
    const restrictedCountries: Country[] = hrRoles
      .filter((role: RoleName) => role.includes('HR_RESTRICTED'))
      .map((role: RoleName) => role.split('_')[2] as Country)

    return hrRoles.map((role: RoleName) => role.split('_')[2] as Country).some((c: Country) => !restrictedCountries.includes(c))
  }

  const getLandingPageByUserRoles = (dashboardEnabled: boolean) => {
    const roles = userRoles ? userRoles : []
    const isCompanyManagerOrHRBP = roles.includes(RoleName.CompanyManager) || roles.includes(RoleName.BusinessPartner)
    const isBuManager = roles.includes(RoleName.BuManager)

    const isClusterOrNetworkManager = roles.includes(RoleName.ClusterManager) || roles.includes(RoleName.NetworkManager)
    return isCompanyManagerOrHRBP
      ? dashboardEnabled
        ? '/dashboard'
        : '/employees'
      : isClusterOrNetworkManager
      ? '/your-company'
      : isBuManager
      ? '/employees'
      : '/myProfile'
  }

  const { employeeLevels, error, loading, hasEmployeeLevel } = useEmployeeLevels(user.country as Country)

  const { data: dataFeatureToggles, error: errorFeatureToggles, loading: loadingFeatureToggles } = useFeatureToggle()

  const { data: dataCompanies, loading: loadingCompanies, error: errorCompanies } = useCompaniesAndCountriesOfCompanyManager(true)

  const { data: kudosVisibilityData, loading: kudosVisibilityLoading, error: kudosVisibilityError } = useAreKudosVisibleForMe()

  const { data: maintenanceData, loading: maintenanceLoading, error: maintenanceError } = useFeatureDisclaimerData()

  if (
    !employeeLevels ||
    loading ||
    error ||
    loadingFeatureToggles ||
    errorFeatureToggles ||
    !dataFeatureToggles ||
    !dataFeatureToggles.featureToggles ||
    !dataCompanies ||
    !dataCompanies.findCompaniesAndCountriesByCompanyManager ||
    loadingCompanies ||
    errorCompanies ||
    kudosVisibilityLoading ||
    kudosVisibilityError ||
    !kudosVisibilityData ||
    !maintenanceData ||
    maintenanceLoading ||
    maintenanceError
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || loadingFeatureToggles || loadingCompanies || kudosVisibilityLoading || maintenanceLoading}
        hasNoData={
          !employeeLevels ||
          !dataFeatureToggles ||
          !dataFeatureToggles.featureToggles ||
          !dataCompanies ||
          !dataCompanies.findCompaniesAndCountriesByCompanyManager ||
          !maintenanceData ||
          !kudosVisibilityData
        }
        error={error || errorFeatureToggles || errorCompanies || kudosVisibilityError || maintenanceError}
      />
    )
  }

  const featureToggles = dataFeatureToggles.featureToggles
  const dashboardEnabled = featureEnabled(FeatureNameEnum.DASHBOARD, featureToggles)

  return (
    <>
      {featureEnabled(FeatureNameEnum.DISCLAIMER, featureToggles) && (
        <Modal
          data-testid="maintenance-modal"
          centered
          title={
            <PerText fontSize="xl" color="primary">
              {!!maintenanceData.featureDisclaimerData ? maintenanceData.featureDisclaimerData.title : t('global.disclaimer.title')}
            </PerText>
          }
          visible={isModalOpen}
          destroyOnClose={true}
          closable={false}
          className="per-modal per-modal--large"
          footer={
            <PerButton
              color="success"
              before="check_stroke"
              variant="pastel"
              type="button"
              text={t('global.button.ok')}
              onClick={() => setIsModalOpen(false)}
            />
          }
        >
          <PerText>
            <FeatureDisclaimerTextComponent
              text={
                !!maintenanceData.featureDisclaimerData
                  ? maintenanceData.featureDisclaimerData.description
                  : t('global.disclaimer.description')
              }
            />
          </PerText>
        </Modal>
      )}
      <SidebarComponent collapsed={collapsed} setCollapsed={setCollapsed}>
        <Menu selectedKeys={['/' + location.pathname.split('/')[1]]} mode="inline">
          {hasPermission(SidebarPermissions.sidemenuProfile, userRoles ? userRoles : []) && (
            <Menu.Item
              key={getLandingPageByUserRoles(dashboardEnabled) === '/myProfile' ? '/' : '/myProfile'}
              data-e2e-navigation="myprofile"
            >
              <Link to={getLandingPageByUserRoles(dashboardEnabled) === '/myProfile' ? '/' : '/myProfile'}>
                <SmartAvatar employeeId={user.employeeId} firstName={user.firstName} lastName={user.lastName} size="small" />
                <span>{t('sideNavigation.profile')}</span>
              </Link>
            </Menu.Item>
          )}
          {dashboardEnabled && hasPermission(SidebarPermissions.sidemenuDashboard, userRoles ? userRoles : []) && (
            <Menu.Item
              key={getLandingPageByUserRoles(dashboardEnabled) === '/dashboard' ? '/' : '/dashboard'}
              data-e2e-navigation="dashboard"
            >
              <Link to={getLandingPageByUserRoles(dashboardEnabled) === '/dashboard' ? '/' : '/dashboard'}>
                <PerIcon name="dashboard" size={iconSize} />
                <span>{t('sideNavigation.dashboard')}</span>
              </Link>
            </Menu.Item>
          )}
          {hasPermission(SidebarPermissions.sidemenuEmployees, userRoles ? userRoles : []) && (
            <Menu.Item
              key={getLandingPageByUserRoles(dashboardEnabled) === '/employees' ? '/' : '/employees'}
              data-e2e-navigation="employees"
            >
              <Link to={getLandingPageByUserRoles(dashboardEnabled) === '/employees' ? '/' : '/employees'}>
                {/* <Badge dot={true}> */}
                <PerIcon name="employees_fill" size={iconSize} />
                {/* </Badge> */}
                <span>{t('sideNavigation.employees')}</span>
              </Link>
            </Menu.Item>
          )}
          {hasPermission(SidebarPermissions.sidemenuOwnGrowth, userRoles ? userRoles : []) &&
            hasEmployeeLevel(user.currentEmployeeLevel as EmployeeLevelEnum) && (
              <Menu.Item key="/own-growth" data-e2e-navigation="own-growth">
                <Link to={`/own-growth`}>
                  <PerIcon name="ownGrowth_fill" size={iconSize} />
                  <span>{t('sideNavigation.ownGrowth')}</span>
                </Link>
              </Menu.Item>
            )}
          {hasPermission(SidebarPermissions.sidemenuMeetings, userRoles ? userRoles : []) &&
            (hasEmployeeLevel(user.currentEmployeeLevel as EmployeeLevelEnum) ||
              hasPermission(MeetingsPagePermissions.reviewerMeetings, userRoles ? userRoles : [])) && (
              <Menu.Item key="/meetings" data-e2e-navigation="meetings">
                <Link to={`/meetings`}>
                  <PerIcon name="chat_fill" size={iconSize} />
                  <span>{t('sideNavigation.meetings')}</span>
                </Link>
              </Menu.Item>
            )}
          {featureEnabled(FeatureNameEnum.KUDOS, featureToggles) &&
            (kudosVisibilityData.areKudosVisibleForMe || hrRolesList.length) &&
            hasPermission(SidebarPermissions.sidemenuKudos, userRoles ? userRoles : []) && (
              <Menu.Item key="/kudos" data-e2e-navigation="kudos">
                <Link to={`/kudos`}>
                  <PerIcon name="clap_fill" size={iconSize} />
                  <span>
                    <span>{t('sideNavigation.kudos')}</span>
                    <span style={{ marginLeft: '6px', fontSize: '8px' }}>Beta</span>
                  </span>
                </Link>
              </Menu.Item>
            )}
          {featureEnabled(FeatureNameEnum.KUDOS, featureToggles) &&
            hasPermission(SidebarPermissions.sidemenuAttentionPoints, userRoles ? userRoles : []) && (
              <Menu.Item key="/attention-points" data-e2e-navigation="attention-points">
                <Link to={`/attention-points`}>
                  <PerIcon name="siren_fill" size={iconSize} />
                  <span>{t('sideNavigation.attentionPoints')}</span>
                </Link>
              </Menu.Item>
            )}
          {hasPermission(SidebarPermissions.sidemenuRanking, userRoles ? userRoles : []) &&
            dataCompanies.findCompaniesAndCountriesByCompanyManager.length > 0 && (
              <Menu.Item key="/rankings" data-e2e-navigation="rankings">
                <Link to={`/rankings`}>
                  <PerIcon name="ranking_fill" size={iconSize} />
                  <span>{t('sideNavigation.rankings')}</span>
                </Link>
              </Menu.Item>
            )}
          {featureEnabled(FeatureNameEnum.DRAFT_SCORE_GRID, featureToggles) &&
            hasPermission(SidebarPermissions.sidemenuScoreGrids, userRoles ? userRoles : []) && (
              <Menu.Item key="/score-grids" data-e2e-navigation="score-grids">
                <Link to={`/score-grids`}>
                  <PerIcon name="notes_fill" size={iconSize} />
                  <span>{t('sideNavigation.scoreGrids')}</span>
                </Link>
              </Menu.Item>
            )}
          {hasPermission(SidebarPermissions.sidemenuPyramids, userRoles ? userRoles : []) && (
            <Menu.Item key="/pyramids" data-e2e-navigation="pyramids">
              <Link to={`/pyramids`}>
                <PerIcon name="statistics_fill" size={iconSize} />
                <span>{t('sideNavigation.pyramids')}</span>
              </Link>
            </Menu.Item>
          )}
          {hasPermission(SidebarPermissions.sidemenuYourCompany, userRoles ? userRoles : []) && (
            <Menu.Item
              key={getLandingPageByUserRoles(dashboardEnabled) === '/your-company' ? '/' : '/your-company'}
              data-e2e-navigation="your-company"
              data-testid="sidebar-your-company"
            >
              <Link to={getLandingPageByUserRoles(dashboardEnabled) === '/your-company' ? '/' : '/your-company'}>
                {/* <Badge dot={true}> */}
                <PerIcon name={'companyOverview_fill'} size={iconSize} />
                {/* </Badge> */}
                <span data-testid="sidebar-your-company-label">{t('sideNavigation.yourCompany')}</span>
              </Link>
            </Menu.Item>
          )}
          {hasPermission(SidebarPermissions.sidemenuAllCompanies, userRoles ? userRoles : []) && (
            <Menu.Item key="/all-companies" data-e2e-navigation="all-companies" data-testid="sidebar-all-companies">
              <Link to={`/all-companies`}>
                {/* <Badge dot={true}> */}
                <PerIcon name={'allCompanies_fill'} size={iconSize} />
                {/* </Badge> */}
                <span data-testid="sidebar-all-companies-label">{t('sideNavigation.allCompanies')}</span>
              </Link>
            </Menu.Item>
          )}
          {isHrAdmin && (
            <Menu.Item key="/hr-settings" data-e2e-navigation="hr-settings" data-testid="sidebar-hr-settings">
              <Link to={`/hr-settings`}>
                {/* <Badge dot={true}> */}
                <PerIcon name={'office_fill'} size={iconSize} />
                {/* </Badge> */}
                <span data-testid="sidebar-hr-settings-label">{t('sideNavigation.hrSettings')}</span>
              </Link>
            </Menu.Item>
          )}
          {isHrAdmin && (
            <Menu.Item key="/hr-support" data-e2e-navigation="hr-support" data-testid="sidebar-hr-support">
              <Link to={`/hr-support`}>
                {/* <Badge dot={true}> */}
                <PerIcon name={'replyServicedesk_fill'} size={iconSize} />
                {/* </Badge> */}
                <span data-testid="sidebar-hr-support-label">{t('sideNavigation.hrSupport')}</span>
              </Link>
            </Menu.Item>
          )}
          {hasPermission(SidebarPermissions.sidemenuGlobalAdmin, userRoles ? userRoles : []) && (
            <Menu.Item key="/global-admin" data-e2e-navigation="global-admin">
              <Link to={`/global-admin`}>
                <PerIcon name="admin_fill" size={iconSize} />
                <span>{t('sideNavigation.globalAdmin')}</span>
              </Link>
            </Menu.Item>
          )}
          {featureEnabled('downloads', featureToggles) &&
            hasPermission(SidebarPermissions.sidemenuDownloads, userRoles ? userRoles : []) &&
            atLeastOneCountryVisible() && (
              <Menu.Item key="/downloads" data-e2e-navigation="downloads">
                <Link to={`/downloads`}>
                  <PerIcon name="download_fill" size={iconSize} />
                  <span>{t('sideNavigation.downloads')}</span>
                </Link>
              </Menu.Item>
            )}
        </Menu>
      </SidebarComponent>
    </>
  )
}
